import { useEffect, useRef, useState } from "react";
import { getPreQuote } from "../api/pricing";
import type { PreQuoteInput, EstimateResult } from "../api/pricing";
import { getEvents, safeCapture } from "../analytics";
import { resolvePreQuoteDisplay, type PreQuoteView } from "../utils/preQuoteDisplay";
import { preQuoteCacheKey } from "../utils/preQuoteCacheKey";

/**
 * Debounced, deduped pre-quote loader. Re-fetches whenever the (stringified)
 * input changes, after a short debounce so we don't fire a request on every
 * keystroke while the user types the amount.
 *
 * A module-level cache (keyed by the stable input signature) is shared across
 * every hook instance. In-flight requests are deduped via a parallel map.
 *
 * Pass `null` to disable the hook (e.g. before the user has entered an amount).
 */

const DEBOUNCE_MS = 500;

const cache = new Map<string, EstimateResult>();
const inFlight = new Map<string, Promise<EstimateResult>>();

/** Drops every cached pre-quote (e.g. on a stale-catalog recovery). */
export const clearPreQuoteCache = (): void => {
  cache.clear();
  inFlight.clear();
};

const fetchWithCache = (
  key: string,
  input: PreQuoteInput
): Promise<EstimateResult> => {
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);
  const existing = inFlight.get(key);
  if (existing) return existing;
  const promise = getPreQuote(input)
    .then((result) => {
      cache.set(key, result);
      inFlight.delete(key);
      const events = getEvents();
      const best = result.summary.best;
      safeCapture(events.PRE_QUOTE_CREATED, {
        success: true,
        options_count: result.summary.count,
        best_capability_key: best?.capabilityKey,
        send_amount: best?.sendAmount,
        receive_amount: best?.receiveAmount,
        customer_fee: best?.customerFee,
        customer_fx_rate: best?.customerFxRate,
        payer_code: best?.payerCode,
        corridor_code: best?.corridorCode,
        funding_method: best?.fundingMethod ?? input.fundingMethod,
        payout_method: best?.payoutMethod ?? input.payoutMethod,
        indicative: true,
        from_country: input.originCountryCode,
        to_country: input.destinationCountryCode,
        amount: input.amount,
        anchor: input.anchor ?? "send",
      });
      safeCapture(events.ROUTE_DETECTED, {
        available: result.summary.count > 0,
        from_country: input.originCountryCode,
        to_country: input.destinationCountryCode,
        amount: input.amount,
      });
      return result;
    })
    .catch((err) => {
      inFlight.delete(key);
      safeCapture(getEvents().PRE_QUOTE_CREATED, {
        success: false,
        error: err instanceof Error ? err.message : "Pre-quote failed",
        error_code: "PRE_QUOTE_FAILED",
        from_country: input.originCountryCode,
        to_country: input.destinationCountryCode,
        amount: input.amount,
      });
      throw err;
    });
  inFlight.set(key, promise);
  return promise;
};

export type UsePreQuoteState = PreQuoteView<EstimateResult>;

type HeldPreQuote = UsePreQuoteState & { key: string | null };

export const usePreQuote = (input: PreQuoteInput | null): UsePreQuoteState => {
  const key = input ? preQuoteCacheKey(input) : null;

  const [held, setHeld] = useState<HeldPreQuote>(() => {
    if (!key) return { key: null, result: null, loading: false, error: null };
    const cached = cache.get(key);
    return cached
      ? { key, result: cached, loading: false, error: null }
      : { key, result: null, loading: true, error: null };
  });

  const inputRef = useRef(input);
  inputRef.current = input;

  useEffect(() => {
    if (!key || !inputRef.current) {
      setHeld({ key: null, result: null, loading: false, error: null });
      return;
    }

    const cached = cache.get(key);
    if (cached) {
      setHeld({ key, result: cached, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setHeld({ key, result: null, loading: true, error: null });

    const timer = window.setTimeout(() => {
      fetchWithCache(key, inputRef.current as PreQuoteInput)
        .then((result) => {
          if (!cancelled) {
            setHeld({ key, result, loading: false, error: null });
          }
        })
        .catch((err: Error) => {
          if (!cancelled) {
            setHeld({ key, result: null, loading: false, error: err });
          }
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // `key` encodes every relevant input — it's the single source of truth for
    // cache identity, so we intentionally omit `input` from the dep list.
  }, [key]);

  return resolvePreQuoteDisplay(
    key,
    held,
    key ? cache.get(key) : undefined
  );
};
